import { useState } from "react";
import { UserCheck, UserX } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useUpdateUser } from "@/hooks/use-resources";

export type UserStatusFilterValue = "active" | "inactive" | "all";

export function UserStatusFilter({ value, onChange }: { value: UserStatusFilterValue; onChange: (v: UserStatusFilterValue) => void }) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as UserStatusFilterValue)}>
      <SelectTrigger className="w-[140px]" data-testid="filter-status">
        <SelectValue placeholder="Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="active">Active only</SelectItem>
        <SelectItem value="inactive">Inactive only</SelectItem>
        <SelectItem value="all">All statuses</SelectItem>
      </SelectContent>
    </Select>
  );
}

type UserRow = { id: number; isActive?: boolean; fullName?: string | null; username: string };

export function UserActivateAction({ user, currentUserId }: { user: UserRow; currentUserId: number | null }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const updateUser = useUpdateUser();
  if (user.id === currentUserId) return null;
  const isActive = user.isActive !== false;
  const nextActive = !isActive;
  const name = user.fullName || user.username;
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        title={isActive ? "Deactivate" : "Reactivate"}
        data-testid={`button-toggle-active-${user.id}`}
      >
        {isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4 text-emerald-600" />}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{nextActive ? "Reactivate" : "Deactivate"} {name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {nextActive
                ? "They will reappear in cleaner and PM assignment dropdowns and can be assigned to new work."
                : "They will be hidden from cleaning-job and PM assignment dropdowns. Past jobs, invoices, and financial reports keep their name and history — nothing is deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-toggle-active">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await updateUser.mutateAsync({ id: user.id, isActive: nextActive } as Parameters<typeof updateUser.mutateAsync>[0]);
                  toast({ title: nextActive ? "User reactivated" : "User deactivated" });
                } catch (err) {
                  const message = err instanceof Error ? err.message : "Update failed";
                  toast({ title: "Error", description: message, variant: "destructive" });
                } finally {
                  setOpen(false);
                }
              }}
              data-testid="button-confirm-toggle-active"
            >
              {nextActive ? "Reactivate" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
